import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '../components/Header';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { 
  FiPlus, 
  FiEdit2, 
  FiTrash2, 
  FiArrowUpRight, 
  FiArrowDownLeft, 
  FiRepeat, 
  FiCheck, 
  FiFilter, 
  FiCalendar, 
  FiDollarSign, 
  FiCreditCard, 
  FiBriefcase, 
  FiUser, 
  FiLayers, 
  FiArrowUp, 
  FiArrowDown,
  FiX,
  FiTrendingUp,
  FiTrendingDown,
  FiSettings
} from 'react-icons/fi';
import { toast } from 'sonner';
import { formatBRL, maskDocument, formatDateBR } from '../utils/format';
import { CrudListDialog } from '../components/CrudListDialog';
import { InactiveSubscriptionCard } from '../components/InactiveSubscriptionCard';
import { EssentialPlanRequiredCard } from '../components/EssentialPlanRequiredCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const CarteirasMovimentacoes = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const ADMIN_EMAILS = ['matheusrweber@gmail.com', 'keylenaiara13@gmail.com'];
  const isAdmin = user?.email && ADMIN_EMAILS.includes(user.email);
  const isTrial = Boolean(
    !isAdmin &&
    user?.plan_type !== 'basic' &&
    user?.plan_type !== 'essential' &&
    (user?.trial_period || user?.plan_type === 'trial' || (user?.trial_ends_at && new Date(user.trial_ends_at) > new Date()))
  );
  const isSubscriptionActive = isAdmin || user?.subscription_status === 'active' || isTrial;
  const isBasic = !isAdmin && isSubscriptionActive && user?.plan_type === 'basic';

  // Wallets data
  const [carteiras, setCarteiras] = useState([]);
  const [selectedCarteiraId, setSelectedCarteiraId] = useState('');
  const [selectedBankId, setSelectedBankId] = useState('all');
  const [loading, setLoading] = useState(true);

  // Global banks & accounts tree for selects
  const [globalBanks, setGlobalBanks] = useState([]);
  const [accountsTree, setAccountsTree] = useState({ entradas: [], saidas: [], total_groups: 0, total_accounts: 0 });

  // Movements data
  const [movimentacoes, setMovimentacoes] = useState([]);
  const [loadingMovements, setLoadingMovements] = useState(false);

  // Movements Filters
  const now = new Date();
  const currentMonthStart = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const currentMonthEnd = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;

  const [startDate, setStartDate] = useState(currentMonthStart);
  const [endDate, setEndDate] = useState(currentMonthEnd);
  const [typeFilter, setTypeFilter] = useState('all');
  const [accountFilter, setAccountFilter] = useState('all');

  // Sorting
  const [sortKey, setSortKey] = useState('reference_date');
  const [sortDir, setSortDir] = useState('desc');

  // Modals state
  const [createWalletOpen, setCreateWalletOpen] = useState(false);
  const [editWalletOpen, setEditWalletOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [quickMovementOpen, setQuickMovementOpen] = useState(false);
  const [banksCatalogDialogOpen, setBanksCatalogDialogOpen] = useState(false);
  const [newCatalogBankName, setNewCatalogBankName] = useState('');

  // Form states
  const [walletForm, setWalletForm] = useState({
    tipo: 'PF',
    documento: '',
    nome: '',
    bancos: [
      { bank_name: '', agencia: '', conta: '', saldo_inicial: '' }
    ]
  });

  const [editWalletForm, setEditWalletForm] = useState({
    carteira_id: '',
    tipo: 'PF',
    documento: '',
    nome: '',
    bancos: []
  });

  const [transferForm, setTransferForm] = useState({
    carteira_id: '',
    banco_origem_id: '',
    banco_destino_id: '',
    valor: '',
    data: new Date().toISOString().split('T')[0],
    observacao: 'Transferência Interna'
  });

  const [quickMovForm, setQuickMovForm] = useState({
    carteira_banco_id: '',
    description: '',
    value: '',
    record_type: 'pagar',
    reference_date: new Date().toISOString().split('T')[0],
    category_name: '',
    carteira_group_id: '',
    carteira_group_name: '',
    carteira_account_id: '',
    carteira_account_name: '',
    is_paid: true
  });

  // Load initial data
  useEffect(() => {
    if (isSubscriptionActive) {
      loadAll();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubscriptionActive]);

  // When selected wallet or bank or filters change, load movements
  useEffect(() => {
    if (isSubscriptionActive && selectedCarteiraId) {
      loadMovimentacoes();
    } else {
      setMovimentacoes([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubscriptionActive, selectedCarteiraId, selectedBankId, startDate, endDate, typeFilter, accountFilter]);

  const loadAll = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadCarteiras(),
        loadGlobalBanks(),
        loadAccountsTree()
      ]);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadCarteiras = async (preferredId = null) => {
    try {
      const res = await axios.get(`${API}/carteiras`, { withCredentials: true });
      const list = res.data || [];
      setCarteiras(list);
      if (list.length > 0) {
        if (preferredId && list.some(c => c.carteira_id === preferredId)) {
          setSelectedCarteiraId(preferredId);
        } else if (!selectedCarteiraId || !list.some(c => c.carteira_id === selectedCarteiraId)) {
          setSelectedCarteiraId(list[0].carteira_id);
        }
      } else {
        setSelectedCarteiraId('');
      }
      return list;
    } catch (err) {
      console.error('Erro ao carregar carteiras:', err);
      toast.error('Erro ao carregar carteiras');
      return [];
    }
  };

  const loadGlobalBanks = async () => {
    try {
      const res = await axios.get(`${API}/banks`, { withCredentials: true });
      setGlobalBanks(res.data || []);
    } catch (err) {
      console.error('Erro ao carregar bancos globais:', err);
    }
  };

  const handleAddCatalogBank = async () => {
    const nameToAdd = newCatalogBankName.trim();
    if (!nameToAdd) return;
    try {
      await axios.post(`${API}/banks`, { name: nameToAdd }, { withCredentials: true });
      setNewCatalogBankName('');
      await loadGlobalBanks();
      toast.success(`Instituição "${nameToAdd}" adicionada ao catálogo!`);
    } catch (err) {
      console.error(err);
      toast.error('Erro ao cadastrar banco no catálogo');
    }
  };

  const handleDeleteCatalogBank = async (bankId) => {
    if (!bankId) return;
    try {
      await axios.delete(`${API}/banks/${bankId}`, { withCredentials: true });
      await loadGlobalBanks();
      toast.success('Instituição removida do catálogo com sucesso!');
    } catch (err) {
      console.error(err);
      toast.error('Erro ao remover banco do catálogo');
    }
  };

  const loadAccountsTree = async () => {
    try {
      const res = await axios.get(`${API}/carteiras/contas/tree`, { withCredentials: true });
      setAccountsTree(res.data || { entradas: [], saidas: [], total_groups: 0, total_accounts: 0 });
    } catch (err) {
      console.error('Erro ao carregar árvore de contas:', err);
    }
  };

  const getSelectedAccountFilterLabel = () => {
    if (!accountFilter || accountFilter === 'all') return 'Todas as Contas';
    if (accountFilter.startsWith('group:')) {
      const gid = accountFilter.replace('group:', '');
      const allG = [...(accountsTree.entradas || []), ...(accountsTree.saidas || [])];
      const found = allG.find(g => g.group_id === gid || g.name === gid);
      return found ? `📁 ${found.name}` : accountFilter;
    }
    if (accountFilter.startsWith('account:')) {
      const aid = accountFilter.replace('account:', '');
      const allG = [...(accountsTree.entradas || []), ...(accountsTree.saidas || [])];
      for (const g of allG) {
        const found = (g.accounts || []).find(a => a.account_id === aid || a.name === aid);
        if (found) return `↳ ${found.name}`;
      }
      return accountFilter;
    }
    return accountFilter;
  };

  const loadMovimentacoes = async () => {
    if (!selectedCarteiraId) return;
    setLoadingMovements(true);
    try {
      const params = new URLSearchParams();
      if (selectedBankId && selectedBankId !== 'all') {
        params.append('banco_item_id', selectedBankId);
      }
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (typeFilter && typeFilter !== 'all') params.append('record_type', typeFilter);
      if (accountFilter && accountFilter !== 'all') {
        params.append('account_filter', accountFilter);
        params.append('category_name', accountFilter);
      }

      const res = await axios.get(`${API}/carteiras/${selectedCarteiraId}/movimentacoes?${params.toString()}`, { withCredentials: true });
      setMovimentacoes(res.data || []);
    } catch (err) {
      console.error('Erro ao carregar movimentações:', err);
      toast.error('Erro ao carregar movimentações da carteira');
    } finally {
      setLoadingMovements(false);
    }
  };

  const currentCarteira = useMemo(() => {
    return carteiras.find(c => c.carteira_id === selectedCarteiraId) || null;
  }, [carteiras, selectedCarteiraId]);

  // Handle Sort
  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const sortedMovimentacoes = useMemo(() => {
    const list = [...movimentacoes];
    list.sort((a, b) => {
      let valA = a[sortKey];
      let valB = b[sortKey];

      if (sortKey === 'final_value' || sortKey === 'value') {
        valA = Number(valA || 0);
        valB = Number(valB || 0);
      } else {
        valA = String(valA || '').toLowerCase();
        valB = String(valB || '').toLowerCase();
      }

      if (valA < valB) return sortDir === 'asc' ? -1 : 1;
      if (valA > valB) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return list;
  }, [movimentacoes, sortKey, sortDir]);

  // Bank summary calculation
  const totalCarteiraSaldo = useMemo(() => {
    if (!currentCarteira || !currentCarteira.bancos) return 0;
    return currentCarteira.bancos.reduce((acc, b) => acc + (Number(b.saldo_atual) || 0), 0);
  }, [currentCarteira]);

  // ============= CREATE WALLET HANDLERS =============
  const handleAddBankToCreate = () => {
    setWalletForm(prev => ({
      ...prev,
      bancos: [...prev.bancos, { bank_name: '', agencia: '', conta: '', saldo_inicial: '' }]
    }));
  };

  const handleRemoveBankFromCreate = (idx) => {
    setWalletForm(prev => ({
      ...prev,
      bancos: prev.bancos.filter((_, i) => i !== idx)
    }));
  };

  const handleBankChangeInCreate = (idx, field, val) => {
    setWalletForm(prev => {
      const copy = [...prev.bancos];
      copy[idx] = { ...copy[idx], [field]: val };
      return { ...prev, bancos: copy };
    });
  };

  const handleSaveWallet = async (e) => {
    e.preventDefault();
    if (!walletForm.nome.trim()) {
      toast.error('Informe o Nome ou Razão Social');
      return;
    }
    if (!walletForm.documento.trim()) {
      toast.error('Informe o CPF ou CNPJ');
      return;
    }
    if (walletForm.bancos.length === 0 || !walletForm.bancos[0].bank_name.trim()) {
      toast.error('Adicione ao menos um banco válido');
      return;
    }

    try {
      const payload = {
        tipo: walletForm.tipo,
        documento: walletForm.documento,
        nome: walletForm.nome,
        bancos: walletForm.bancos.filter(b => b.bank_name.trim()).map(b => ({
          bank_name: b.bank_name.trim(),
          agencia: b.agencia.trim(),
          conta: b.conta.trim(),
          saldo_inicial: parseFloat(String(b.saldo_inicial).replace(',', '.')) || 0
        }))
      };

      const res = await axios.post(`${API}/carteiras`, payload, { withCredentials: true });
      toast.success('Carteira cadastrada com sucesso!');
      setCreateWalletOpen(false);
      setWalletForm({
        tipo: 'PF',
        documento: '',
        nome: '',
        bancos: [{ bank_name: '', agencia: '', conta: '', saldo_inicial: '' }]
      });
      await loadCarteiras(res.data?.carteira_id);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Erro ao cadastrar carteira');
    }
  };

  // ============= EDIT WALLET HANDLERS =============
  const handleOpenEditWallet = () => {
    if (!currentCarteira) {
      toast.error('Nenhuma carteira selecionada');
      return;
    }
    setEditWalletForm({
      carteira_id: currentCarteira.carteira_id,
      tipo: currentCarteira.tipo || 'PF',
      documento: currentCarteira.documento || '',
      nome: currentCarteira.nome || '',
      bancos: (currentCarteira.bancos || []).map(b => ({
        banco_item_id: b.banco_item_id,
        bank_name: b.bank_name,
        agencia: b.agencia || '',
        conta: b.conta || '',
        saldo_inicial: b.saldo_inicial != null ? String(b.saldo_inicial) : '0'
      }))
    });
    setEditWalletOpen(true);
  };

  const handleSelectWalletToEdit = (cid) => {
    const w = carteiras.find(c => c.carteira_id === cid);
    if (!w) return;
    setEditWalletForm({
      carteira_id: w.carteira_id,
      tipo: w.tipo || 'PF',
      documento: w.documento || '',
      nome: w.nome || '',
      bancos: (w.bancos || []).map(b => ({
        banco_item_id: b.banco_item_id,
        bank_name: b.bank_name,
        agencia: b.agencia || '',
        conta: b.conta || '',
        saldo_inicial: b.saldo_inicial != null ? String(b.saldo_inicial) : '0'
      }))
    });
  };

  const handleAddBankToEdit = () => {
    setEditWalletForm(prev => ({
      ...prev,
      bancos: [...prev.bancos, { bank_name: '', agencia: '', conta: '', saldo_inicial: '0' }]
    }));
  };

  const handleRemoveBankFromEdit = (idx) => {
    setEditWalletForm(prev => ({
      ...prev,
      bancos: prev.bancos.filter((_, i) => i !== idx)
    }));
  };

  const handleBankChangeInEdit = (idx, field, val) => {
    setEditWalletForm(prev => {
      const copy = [...prev.bancos];
      copy[idx] = { ...copy[idx], [field]: val };
      return { ...prev, bancos: copy };
    });
  };

  const handleSaveEditedWallet = async (e) => {
    e.preventDefault();
    if (!editWalletForm.nome.trim()) {
      toast.error('Informe o Nome ou Razão Social');
      return;
    }

    try {
      const payload = {
        tipo: editWalletForm.tipo,
        documento: editWalletForm.documento,
        nome: editWalletForm.nome,
        bancos: editWalletForm.bancos.filter(b => b.bank_name.trim()).map(b => ({
          banco_item_id: b.banco_item_id || undefined,
          bank_name: b.bank_name.trim(),
          agencia: b.agencia.trim(),
          conta: b.conta.trim(),
          saldo_inicial: parseFloat(String(b.saldo_inicial).replace(',', '.')) || 0
        }))
      };

      await axios.put(`${API}/carteiras/${editWalletForm.carteira_id}`, payload, { withCredentials: true });
      toast.success('Carteira atualizada com sucesso!');
      setEditWalletOpen(false);
      await loadCarteiras(editWalletForm.carteira_id);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Erro ao atualizar carteira');
    }
  };

  const handleDeleteWallet = async () => {
    if (!editWalletForm.carteira_id) return;
    if (!window.confirm('Tem certeza que deseja excluir esta carteira? As movimentações vinculadas perderão o vínculo com a carteira.')) return;

    try {
      await axios.delete(`${API}/carteiras/${editWalletForm.carteira_id}`, { withCredentials: true });
      toast.success('Carteira excluída');
      setEditWalletOpen(false);
      await loadCarteiras();
    } catch (err) {
      console.error(err);
      toast.error('Erro ao excluir carteira');
    }
  };

  // ============= INTERNAL TRANSFER HANDLERS =============
  const handleOpenTransfer = () => {
    if (!currentCarteira || !currentCarteira.bancos || currentCarteira.bancos.length < 2) {
      toast.error('A carteira precisa ter pelo menos 2 bancos cadastrados para realizar transferência interna');
      return;
    }

    const b1 = currentCarteira.bancos[0]?.banco_item_id;
    const b2 = currentCarteira.bancos[1]?.banco_item_id;

    setTransferForm({
      carteira_id: currentCarteira.carteira_id,
      banco_origem_id: b1 || '',
      banco_destino_id: b2 || '',
      valor: '',
      data: new Date().toISOString().split('T')[0],
      observacao: 'Transferência Interna'
    });
    setTransferOpen(true);
  };

  const handleExecuteTransfer = async (e) => {
    e.preventDefault();
    if (!transferForm.banco_origem_id || !transferForm.banco_destino_id) {
      toast.error('Selecione os bancos de origem e destino');
      return;
    }
    if (transferForm.banco_origem_id === transferForm.banco_destino_id) {
      toast.error('O banco de origem e destino não podem ser iguais');
      return;
    }
    const val = parseFloat(String(transferForm.valor).replace(',', '.'));
    if (!val || val <= 0) {
      toast.error('Informe um valor válido maior que zero');
      return;
    }

    try {
      const payload = {
        carteira_id: transferForm.carteira_id,
        banco_origem_id: transferForm.banco_origem_id,
        banco_destino_id: transferForm.banco_destino_id,
        valor: val,
        data: transferForm.data,
        observacao: transferForm.observacao
      };

      await axios.post(`${API}/carteiras/transferencia`, payload, { withCredentials: true });
      toast.success('Transferência interna realizada com sucesso!');
      setTransferOpen(false);
      await loadCarteiras(currentCarteira.carteira_id);
      loadMovimentacoes();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Erro ao realizar transferência');
    }
  };

  // ============= QUICK MOVEMENT HANDLERS =============
  const handleOpenQuickMovement = (bancoId = null) => {
    if (!currentCarteira || !currentCarteira.bancos || currentCarteira.bancos.length === 0) {
      toast.error('Cadastre bancos na carteira primeiro');
      return;
    }
    const targetBanco = bancoId || (selectedBankId !== 'all' ? selectedBankId : currentCarteira.bancos[0]?.banco_item_id);
    setQuickMovForm({
      carteira_banco_id: targetBanco || currentCarteira.bancos[0]?.banco_item_id,
      description: '',
      value: '',
      record_type: 'pagar',
      reference_date: new Date().toISOString().split('T')[0],
      category_name: '',
      carteira_group_id: '',
      carteira_group_name: '',
      carteira_account_id: '',
      carteira_account_name: '',
      is_paid: true
    });
    setQuickMovementOpen(true);
  };

  const handleSaveQuickMovement = async (e) => {
    e.preventDefault();
    if (!quickMovForm.carteira_banco_id) {
      toast.error('Selecione o banco da movimentação');
      return;
    }
    const val = parseFloat(String(quickMovForm.value).replace(',', '.'));
    if (!val || val <= 0) {
      toast.error('Informe um valor válido');
      return;
    }

    try {
      const payload = {
        carteira_banco_id: quickMovForm.carteira_banco_id,
        description: quickMovForm.description.trim() || 'Lançamento Manual',
        value: val,
        record_type: quickMovForm.record_type,
        reference_date: quickMovForm.reference_date,
        due_date: quickMovForm.reference_date,
        category_name: quickMovForm.carteira_account_name || quickMovForm.category_name || 'Geral',
        carteira_group_id: quickMovForm.carteira_group_id || null,
        carteira_group_name: quickMovForm.carteira_group_name || null,
        carteira_account_id: quickMovForm.carteira_account_id || null,
        carteira_account_name: quickMovForm.carteira_account_name || null,
        is_paid: quickMovForm.is_paid
      };

      await axios.post(`${API}/carteiras/${selectedCarteiraId}/movimentacoes`, payload, { withCredentials: true });
      toast.success('Movimentação registrada com sucesso!');
      setQuickMovementOpen(false);
      await loadCarteiras(selectedCarteiraId);
      loadMovimentacoes();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Erro ao registrar movimentação');
    }
  };

  // Delete movement
  const handleDeleteMovement = async (recId) => {
    if (!window.confirm('Deseja excluir este lançamento? Se for transferência interna, ambos os lados serão excluídos.')) return;
    try {
      await axios.delete(`${API}/carteiras/${selectedCarteiraId}/movimentacoes/${recId}`, { withCredentials: true });
      toast.success('Lançamento excluído');
      await loadCarteiras(selectedCarteiraId);
      loadMovimentacoes();
    } catch (err) {
      console.error(err);
      toast.error('Erro ao excluir lançamento');
    }
  };

  // Toggle paid status
  const handleTogglePaid = async (rec) => {
    try {
      await axios.put(`${API}/financial-records/${rec.record_id}`, { is_paid: !rec.is_paid }, { withCredentials: true });
      await loadCarteiras(selectedCarteiraId);
      loadMovimentacoes();
    } catch (err) {
      console.error(err);
      toast.error('Erro ao atualizar status de pagamento');
    }
  };

  if (!isSubscriptionActive) {
    return (
      <div className="min-h-screen bg-[#F0F5FA] dark:bg-[#070D1A] transition-colors pb-16">
        <Header />
        <main className="transition-all duration-300">
          <InactiveSubscriptionCard moduleName="Carteiras" />
        </main>
      </div>
    );
  }

  if (isBasic) {
    return (
      <div className="min-h-screen bg-[#F0F5FA] dark:bg-[#070D1A] transition-colors pb-16">
        <Header />
        <main className="transition-all duration-300">
          <EssentialPlanRequiredCard module="carteiras" />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50/50 dark:bg-slate-950">
      <Header />

      <main className="transition-all duration-300">
        <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto space-y-6">
        
        {/* ================= TOP SECTION: WALLET SELECTOR & QUICK ACTIONS ================= */}
        <div className="bg-white dark:bg-slate-900 rounded-2xl p-4 sm:p-6 border border-slate-200/80 dark:border-slate-800 shadow-sm transition-all">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            
            {/* Left: Wallet Dropdown Selector */}
            <div className="flex-1 max-w-xl">
              <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-1.5">
                Carteira Selecionada
              </label>
              {carteiras.length > 0 ? (
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-blue-600/10 text-blue-600 dark:text-blue-400 flex items-center justify-center flex-shrink-0">
                    <FiBriefcase size={20} />
                  </div>
                  <Select value={selectedCarteiraId} onValueChange={(val) => { setSelectedCarteiraId(val); setSelectedBankId('all'); }}>
                    <SelectTrigger className="h-11 bg-slate-50 dark:bg-slate-800/60 border-slate-200 dark:border-slate-700 text-sm font-semibold text-slate-800 dark:text-slate-100">
                      <SelectValue placeholder="Selecione uma carteira" />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      {carteiras.map((c) => (
                        <SelectItem key={c.carteira_id} value={c.carteira_id}>
                          <div className="flex items-center gap-2 py-0.5">
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                              c.tipo === 'PJ' ? 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300' : 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300'
                            }`}>
                              {c.tipo || 'PF'}
                            </span>
                            <span className="font-medium text-slate-800 dark:text-slate-200">{c.nome}</span>
                            <span className="text-xs text-slate-400">({maskDocument(c.documento, c.tipo)})</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div className="flex items-center gap-3 p-3 bg-amber-50 dark:bg-amber-950/40 rounded-xl border border-amber-200 dark:border-amber-900/50 text-amber-800 dark:text-amber-300 text-sm">
                  <FiBriefcase className="flex-shrink-0" />
                  <span>Nenhuma carteira cadastrada. Cadastre sua primeira carteira para começar!</span>
                </div>
              )}
            </div>

            {/* Right: Quick Action Buttons in 2x2 Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 w-full lg:w-auto">
              {/* Linha 1 - Esquerda: Cadastrar Carteira */}
              <Button
                onClick={() => setCreateWalletOpen(true)}
                className="bg-blue-900 hover:bg-blue-800 text-white font-semibold rounded-xl gap-2 shadow-sm h-10 w-full justify-center"
              >
                <FiPlus size={18} /> Cadastrar Carteira
              </Button>

              {/* Linha 1 - Direita: Editar Carteira */}
              <Button
                variant="outline"
                disabled={!currentCarteira}
                onClick={handleOpenEditWallet}
                className="border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl gap-2 h-10 font-semibold w-full justify-center"
              >
                <FiEdit2 size={16} /> Editar Carteira
              </Button>

              {/* Linha 2 - Esquerda: Transferência Interna */}
              <Button
                variant="outline"
                disabled={!currentCarteira || !currentCarteira.bancos || currentCarteira.bancos.length < 2}
                onClick={handleOpenTransfer}
                className="border-emerald-500/30 text-emerald-700 dark:text-emerald-400 bg-emerald-50/50 dark:bg-emerald-950/30 hover:bg-emerald-100 dark:hover:bg-emerald-900/40 rounded-xl gap-2 font-medium h-10 w-full justify-center"
                title={currentCarteira?.bancos?.length < 2 ? 'Necessário pelo menos 2 bancos na carteira' : 'Transferir entre contas da carteira'}
              >
                <FiRepeat size={16} /> Transferência Interna
              </Button>

              {/* Linha 2 - Direita: Contas */}
              <Button
                variant="outline"
                onClick={() => navigate('/carteiras/contas')}
                className="border-blue-500/30 text-blue-700 dark:text-cyan-400 bg-blue-50/70 dark:bg-blue-950/40 hover:bg-blue-100 dark:hover:bg-blue-900/50 rounded-xl gap-2 font-semibold h-10 shadow-xs transition-all w-full justify-center"
                title="Acessar o Plano de Contas e Gerenciar a Árvore de Contas"
              >
                <FiLayers size={16} className="text-blue-600 dark:text-cyan-400" /> Contas
              </Button>
            </div>

          </div>

          {/* Current Wallet Metadata Bar */}
          {currentCarteira && (
            <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-800/80 flex flex-wrap items-center justify-between gap-3 text-xs text-slate-500 dark:text-slate-400">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-slate-700 dark:text-slate-300">Documento:</span>
                <span className="font-mono bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded text-slate-700 dark:text-slate-300">
                  {maskDocument(currentCarteira.documento, currentCarteira.tipo)}
                </span>
                <span className="text-slate-300 dark:text-slate-700">•</span>
                <span className="font-semibold text-slate-700 dark:text-slate-300">Tipo:</span>
                <span className="font-medium">{currentCarteira.tipo === 'PJ' ? 'Pessoa Jurídica' : 'Pessoa Física'}</span>
              </div>

              <div className="flex items-center gap-2 font-medium">
                <span>Saldo Consolidado da Carteira:</span>
                <span className={`text-sm font-bold ${totalCarteiraSaldo >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
                  {formatBRL(totalCarteiraSaldo)}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* ================= BANK CARDS GRID ================= */}
        {currentCarteira && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300 flex items-center gap-2">
                  <FiCreditCard className="text-blue-600" /> Bancos Vinculados ({currentCarteira.bancos?.length || 0})
                </h3>
                <span className="text-xs text-slate-400 hidden sm:inline">— Clique no card para filtrar a tabela de lançamentos</span>
              </div>

              {selectedBankId !== 'all' && (
                <button
                  onClick={() => setSelectedBankId('all')}
                  className="text-xs font-semibold text-blue-600 hover:text-blue-800 dark:text-cyan-400 hover:underline flex items-center gap-1"
                >
                  <FiX size={14} /> Mostrar todos os bancos
                </button>
              )}
            </div>

            {currentCarteira.bancos && currentCarteira.bancos.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                
                {/* "Todos os Bancos" Summary Card */}
                <div
                  onClick={() => setSelectedBankId('all')}
                  className={`p-4 rounded-2xl cursor-pointer transition-all border ${
                    selectedBankId === 'all'
                      ? 'bg-gradient-to-br from-blue-900 to-indigo-900 text-white border-blue-700 shadow-md ring-2 ring-blue-500/30'
                      : 'bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-800/80 border-slate-200 dark:border-slate-800 text-slate-800 dark:text-slate-100 shadow-sm'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                        selectedBankId === 'all' ? 'bg-white/20 text-white' : 'bg-blue-100 dark:bg-blue-950 text-blue-600'
                      }`}>
                        <FiLayers size={16} />
                      </div>
                      <span className="font-bold text-sm">Visão Geral</span>
                    </div>
                    {selectedBankId === 'all' && (
                      <span className="text-[10px] font-bold uppercase bg-white/20 px-2 py-0.5 rounded-full">Ativo</span>
                    )}
                  </div>
                  <p className={`text-xs ${selectedBankId === 'all' ? 'text-blue-200' : 'text-slate-400'} mb-3`}>
                    Todos os bancos da carteira
                  </p>
                  <div>
                    <span className={`text-[10px] uppercase font-bold tracking-wider ${selectedBankId === 'all' ? 'text-blue-200' : 'text-slate-400'}`}>
                      Saldo Total
                    </span>
                    <p className={`text-xl font-black ${
                      selectedBankId === 'all'
                        ? 'text-white'
                        : totalCarteiraSaldo >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'
                    }`}>
                      {formatBRL(totalCarteiraSaldo)}
                    </p>
                  </div>
                </div>

                {/* Specific Bank Cards */}
                {currentCarteira.bancos.map((banco) => {
                  const isSelected = selectedBankId === banco.banco_item_id;
                  const saldoAtual = Number(banco.saldo_atual) || 0;
                  const saldoInicial = Number(banco.saldo_inicial) || 0;

                  return (
                    <div
                      key={banco.banco_item_id}
                      onClick={() => setSelectedBankId(isSelected ? 'all' : banco.banco_item_id)}
                      className={`p-4 rounded-2xl cursor-pointer transition-all border relative overflow-hidden group ${
                        isSelected
                          ? 'bg-gradient-to-br from-blue-900 via-[#122B68] to-slate-900 text-white border-blue-600 shadow-lg ring-2 ring-cyan-400/40'
                          : 'bg-white dark:bg-slate-900 hover:border-blue-300 dark:hover:border-blue-700 border-slate-200 dark:border-slate-800 text-slate-800 dark:text-slate-100 shadow-sm'
                      }`}
                    >
                      {/* Top Bank Name and Account info */}
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="flex items-center gap-2.5 overflow-hidden">
                          <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 font-black text-sm shadow-sm ${
                            isSelected ? 'bg-cyan-500 text-white' : 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700'
                          }`}>
                            {banco.bank_name?.substring(0, 2).toUpperCase() || 'BK'}
                          </div>
                          <div className="overflow-hidden">
                            <h4 className="font-bold text-sm truncate leading-snug">{banco.bank_name}</h4>
                            <p className={`text-[11px] truncate ${isSelected ? 'text-cyan-200' : 'text-slate-400'}`}>
                              {banco.agencia ? `Ag: ${banco.agencia}` : ''} {banco.conta ? `• CC: ${banco.conta}` : ''}
                            </p>
                          </div>
                        </div>

                        {isSelected ? (
                          <span className="text-[10px] font-black uppercase bg-cyan-400 text-slate-900 px-2 py-0.5 rounded-full flex-shrink-0">
                            Filtrando
                          </span>
                        ) : (
                          <span className="text-slate-400 group-hover:text-blue-500 transition-colors">
                            <FiCreditCard size={16} />
                          </span>
                        )}
                      </div>

                      {/* Balances */}
                      <div className="grid grid-cols-2 gap-2 mt-3 pt-2.5 border-t border-slate-100 dark:border-slate-800/80">
                        <div>
                          <span className={`text-[10px] font-semibold uppercase tracking-wider block ${isSelected ? 'text-slate-300' : 'text-slate-400'}`}>
                            Saldo Inicial
                          </span>
                          <span className={`text-xs font-semibold ${isSelected ? 'text-slate-200' : 'text-slate-600 dark:text-slate-300'}`}>
                            {formatBRL(saldoInicial)}
                          </span>
                        </div>
                        <div className="text-right">
                          <span className={`text-[10px] font-semibold uppercase tracking-wider block ${isSelected ? 'text-cyan-200' : 'text-slate-400'}`}>
                            Saldo Atual
                          </span>
                          <span className={`text-sm font-black ${
                            isSelected 
                              ? (saldoAtual >= 0 ? 'text-cyan-300' : 'text-rose-300')
                              : (saldoAtual >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400')
                          }`}>
                            {formatBRL(saldoAtual)}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}

              </div>
            ) : (
              <div className="p-6 bg-slate-100 dark:bg-slate-900 rounded-2xl text-center text-slate-500 text-sm">
                Nenhum banco vinculado a esta carteira. Clique em "Editar Carteira" para adicionar bancos.
              </div>
            )}
          </div>
        )}

        {/* ================= MOVEMENTS VIEW (TABLE & FILTERS) ================= */}
        {currentCarteira && (
          <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200/80 dark:border-slate-800 shadow-sm overflow-hidden space-y-4 p-4 sm:p-6">
            
            {/* Header & Filter Controls */}
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 pb-4 border-b border-slate-100 dark:border-slate-800">
              <div>
                <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                  Movimentações Financeiras
                  {selectedBankId !== 'all' && (
                    <Badge variant="outline" className="text-xs bg-blue-50 text-blue-800 border-blue-200 dark:bg-blue-950 dark:text-cyan-300 dark:border-blue-800">
                      Banco: {currentCarteira.bancos?.find(b => b.banco_item_id === selectedBankId)?.bank_name}
                    </Badge>
                  )}
                </h3>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                  Lançamentos vinculados à carteira e recálculo dinâmico do saldo bancário
                </p>
              </div>

              {/* Action: Quick Movement inside wallet */}
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => handleOpenQuickMovement()}
                  className="bg-blue-900 hover:bg-blue-800 text-white rounded-xl text-xs gap-1.5 h-9"
                >
                  <FiPlus size={14} /> Novo Lançamento na Carteira
                </Button>
              </div>
            </div>

            {/* Filter Bar */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 bg-slate-50 dark:bg-slate-800/40 p-3.5 rounded-xl border border-slate-200/60 dark:border-slate-700/60">
              {/* Date Initial */}
              <div>
                <Label className="text-[11px] font-semibold text-slate-600 dark:text-slate-300 uppercase">Data Inicial</Label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="h-9 text-xs mt-1 bg-white dark:bg-slate-900"
                />
              </div>

              {/* Date Final */}
              <div>
                <Label className="text-[11px] font-semibold text-slate-600 dark:text-slate-300 uppercase">Data Final</Label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="h-9 text-xs mt-1 bg-white dark:bg-slate-900"
                />
              </div>

              {/* Type Filter */}
              <div>
                <Label className="text-[11px] font-semibold text-slate-600 dark:text-slate-300 uppercase">Tipo</Label>
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger className="h-9 text-xs mt-1 bg-white dark:bg-slate-900">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas as Movimentações</SelectItem>
                    <SelectItem value="receber">Entradas (Receitas)</SelectItem>
                    <SelectItem value="pagar">Saídas (Despesas)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Account Tree Dropdown Filter */}
              <div>
                <Label className="text-[11px] font-semibold text-slate-600 dark:text-slate-300 uppercase">Contas</Label>
                <Select value={accountFilter} onValueChange={setAccountFilter}>
                  <SelectTrigger className="h-9 text-xs mt-1 bg-white dark:bg-slate-900 font-medium">
                    <SelectValue placeholder="Todas as Contas">
                      {getSelectedAccountFilterLabel()}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent className="max-h-80">
                    <SelectItem value="all">
                      <div className="flex items-center gap-2 font-medium">
                        <FiLayers size={14} className="text-slate-400" />
                        <span>Todas as Contas</span>
                      </div>
                    </SelectItem>

                    {/* 1 - Entradas */}
                    {(accountsTree.entradas || []).length > 0 && (
                      <div className="pt-2 pb-1 px-2.5 text-[10px] font-black uppercase tracking-wider text-emerald-700 dark:text-emerald-400 bg-emerald-50/90 dark:bg-emerald-950/80 sticky top-0 z-10 flex items-center gap-1.5 border-y border-emerald-200/60 dark:border-emerald-800/60">
                        <FiTrendingUp size={12} />
                        <span>1 - ENTRADAS</span>
                      </div>
                    )}
                    {(accountsTree.entradas || []).map((grp) => (
                      <React.Fragment key={grp.group_id}>
                        <SelectItem
                          value={`group:${grp.group_id}`}
                          className="font-bold text-slate-900 dark:text-slate-100 bg-slate-100/70 dark:bg-slate-800/70 my-0.5 border-l-2 border-emerald-500 pl-3 py-1.5 cursor-pointer"
                        >
                          <div className="flex items-center justify-between w-full gap-2">
                            <div className="flex items-center gap-1.5">
                              <span>📁</span>
                              <span>{grp.name}</span>
                            </div>
                            <span className="text-[9px] text-emerald-700 dark:text-emerald-300 bg-emerald-100 dark:bg-emerald-950 px-1.5 py-0.5 rounded font-bold uppercase">
                              Categoria
                            </span>
                          </div>
                        </SelectItem>
                        {(grp.accounts || []).map((acc) => (
                          <SelectItem
                            key={acc.account_id}
                            value={`account:${acc.account_id}`}
                            className="pl-7 text-xs font-medium text-slate-700 dark:text-slate-300 py-1 cursor-pointer"
                          >
                            <div className="flex items-center gap-1.5">
                              <span className="text-slate-400">↳</span>
                              <span>{acc.name}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </React.Fragment>
                    ))}

                    {/* 2 - Saídas */}
                    {(accountsTree.saidas || []).length > 0 && (
                      <div className="pt-2 pb-1 px-2.5 text-[10px] font-black uppercase tracking-wider text-rose-700 dark:text-rose-400 bg-rose-50/90 dark:bg-rose-950/80 sticky top-0 z-10 flex items-center gap-1.5 border-y border-rose-200/60 dark:border-rose-800/60 mt-1.5">
                        <FiTrendingDown size={12} />
                        <span>2 - SAÍDAS</span>
                      </div>
                    )}
                    {(accountsTree.saidas || []).map((grp) => (
                      <React.Fragment key={grp.group_id}>
                        <SelectItem
                          value={`group:${grp.group_id}`}
                          className="font-bold text-slate-900 dark:text-slate-100 bg-slate-100/70 dark:bg-slate-800/70 my-0.5 border-l-2 border-rose-500 pl-3 py-1.5 cursor-pointer"
                        >
                          <div className="flex items-center justify-between w-full gap-2">
                            <div className="flex items-center gap-1.5">
                              <span>📁</span>
                              <span>{grp.name}</span>
                            </div>
                            <span className="text-[9px] text-rose-700 dark:text-rose-300 bg-rose-100 dark:bg-rose-950 px-1.5 py-0.5 rounded font-bold uppercase">
                              Categoria
                            </span>
                          </div>
                        </SelectItem>
                        {(grp.accounts || []).map((acc) => (
                          <SelectItem
                            key={acc.account_id}
                            value={`account:${acc.account_id}`}
                            className="pl-7 text-xs font-medium text-slate-700 dark:text-slate-300 py-1 cursor-pointer"
                          >
                            <div className="flex items-center gap-1.5">
                              <span className="text-slate-400">↳</span>
                              <span>{acc.name}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </React.Fragment>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Table */}
            <div className="rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden">
              <Table>
                <TableHeader className="bg-slate-50 dark:bg-slate-800/80">
                  <TableRow>
                    <TableHead 
                      onClick={() => handleSort('reference_date')}
                      className="cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700/50 font-bold text-slate-700 dark:text-slate-200 text-xs"
                    >
                      <div className="flex items-center gap-1">
                        Data {sortKey === 'reference_date' && (sortDir === 'asc' ? <FiArrowUp size={12} /> : <FiArrowDown size={12} />)}
                      </div>
                    </TableHead>

                    <TableHead 
                      onClick={() => handleSort('description')}
                      className="cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700/50 font-bold text-slate-700 dark:text-slate-200 text-xs"
                    >
                      <div className="flex items-center gap-1">
                        Descrição {sortKey === 'description' && (sortDir === 'asc' ? <FiArrowUp size={12} /> : <FiArrowDown size={12} />)}
                      </div>
                    </TableHead>

                    <TableHead className="font-bold text-slate-700 dark:text-slate-200 text-xs">
                      Banco
                    </TableHead>

                    <TableHead 
                      onClick={() => handleSort('category_name')}
                      className="cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700/50 font-bold text-slate-700 dark:text-slate-200 text-xs"
                    >
                      <div className="flex items-center gap-1">
                        Conta / Categoria {sortKey === 'category_name' && (sortDir === 'asc' ? <FiArrowUp size={12} /> : <FiArrowDown size={12} />)}
                      </div>
                    </TableHead>

                    <TableHead 
                      onClick={() => handleSort('record_type')}
                      className="cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700/50 font-bold text-slate-700 dark:text-slate-200 text-xs"
                    >
                      <div className="flex items-center gap-1">
                        Tipo {sortKey === 'record_type' && (sortDir === 'asc' ? <FiArrowUp size={12} /> : <FiArrowDown size={12} />)}
                      </div>
                    </TableHead>

                    <TableHead 
                      onClick={() => handleSort('final_value')}
                      className="cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700/50 font-bold text-slate-700 dark:text-slate-200 text-xs text-right"
                    >
                      <div className="flex items-center justify-end gap-1">
                        Valor {sortKey === 'final_value' && (sortDir === 'asc' ? <FiArrowUp size={12} /> : <FiArrowDown size={12} />)}
                      </div>
                    </TableHead>

                    <TableHead className="text-right font-bold text-slate-700 dark:text-slate-200 text-xs">
                      Ações
                    </TableHead>
                  </TableRow>
                </TableHeader>

                <TableBody>
                  {loadingMovements ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-12 text-slate-500">
                        Carregando movimentações...
                      </TableCell>
                    </TableRow>
                  ) : sortedMovimentacoes.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-12 text-slate-500">
                        Nenhuma movimentação encontrada para os filtros selecionados.
                      </TableCell>
                    </TableRow>
                  ) : (
                    sortedMovimentacoes.map((rec) => {
                      const isEntrada = rec.record_type === 'receber';
                      const val = Number(rec.final_value ?? rec.value ?? 0);

                      return (
                        <TableRow key={rec.record_id} className="hover:bg-slate-50/70 dark:hover:bg-slate-800/40">
                          {/* Date */}
                          <TableCell className="text-xs font-mono text-slate-600 dark:text-slate-300">
                            {formatDateBR(rec.reference_date || rec.payment_date || rec.due_date)}
                          </TableCell>

                          {/* Description */}
                          <TableCell className="text-xs font-medium text-slate-900 dark:text-slate-100">
                            <div className="flex items-center gap-2">
                              <span>{rec.description}</span>
                              {rec.transfer_id && (
                                <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-950 dark:text-purple-300">
                                  Transferência
                                </Badge>
                              )}
                            </div>
                          </TableCell>

                          {/* Bank */}
                          <TableCell className="text-xs text-slate-600 dark:text-slate-300">
                            {rec.carteira_banco_name || rec.bank_name || '-'}
                          </TableCell>

                          {/* Account / Category with Group */}
                          <TableCell className="text-xs text-slate-600 dark:text-slate-300">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              {rec.carteira_group_name && (
                                <span className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded border border-slate-200/50 dark:border-slate-700">
                                  {rec.carteira_group_name}
                                </span>
                              )}
                              <span className="font-medium text-slate-800 dark:text-slate-200">
                                {rec.carteira_account_name || rec.category_name || '-'}
                              </span>
                            </div>
                          </TableCell>

                          {/* Type */}
                          <TableCell>
                            {isEntrada ? (
                              <Badge className="bg-emerald-100 dark:bg-emerald-950 text-emerald-800 dark:text-emerald-300 hover:bg-emerald-100 text-[11px] gap-1 font-bold">
                                <FiArrowUpRight size={13} /> Entrada
                              </Badge>
                            ) : (
                              <Badge className="bg-rose-100 dark:bg-rose-950 text-rose-800 dark:text-rose-300 hover:bg-rose-100 text-[11px] gap-1 font-bold">
                                <FiArrowDownLeft size={13} /> Saída
                              </Badge>
                            )}
                          </TableCell>

                          {/* Value */}
                          <TableCell className={`text-right text-xs font-bold ${isEntrada ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
                            {isEntrada ? `+ ${formatBRL(val)}` : `- ${formatBRL(val)}`}
                          </TableCell>

                          {/* Actions */}
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1.5">
                              <button
                                onClick={() => handleTogglePaid(rec)}
                                title={rec.is_paid ? 'Marcar como não pago' : 'Marcar como pago'}
                                className={`p-1.5 rounded-lg text-xs font-semibold transition-colors ${
                                  rec.is_paid
                                    ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300'
                                    : 'bg-slate-100 text-slate-500 hover:bg-slate-200 dark:bg-slate-800'
                                }`}
                              >
                                <FiCheck size={14} />
                              </button>

                              <button
                                onClick={() => handleDeleteMovement(rec.record_id)}
                                title="Excluir movimentação"
                                className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/50 transition-colors"
                              >
                                <FiTrash2 size={14} />
                              </button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>

          </div>
        )}
        </div>

      </main>

      {/* ================= MODAL: CADASTRAR CARTEIRA ================= */}
      <Dialog open={createWalletOpen} onOpenChange={setCreateWalletOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-blue-900 dark:text-blue-400 flex items-center gap-2">
              <FiBriefcase /> Cadastrar Carteira
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Cadastre uma carteira (Pessoa Física ou Jurídica) e vincule seus bancos e saldos iniciais.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSaveWallet} className="space-y-5 mt-4">
            {/* Tipo: Radio Group PF / PJ */}
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">
                Tipo de Carteira
              </Label>
              <RadioGroup
                value={walletForm.tipo}
                onValueChange={(val) => setWalletForm({ ...walletForm, tipo: val, documento: maskDocument(walletForm.documento, val) })}
                className="flex gap-4 mt-2"
              >
                <div className="flex items-center space-x-2 bg-slate-50 dark:bg-slate-800 px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 cursor-pointer flex-1">
                  <RadioGroupItem value="PF" id="create-pf" />
                  <Label htmlFor="create-pf" className="cursor-pointer font-semibold text-slate-800 dark:text-slate-200 flex items-center gap-1.5">
                    <FiUser className="text-blue-600" /> Pessoa Física (PF)
                  </Label>
                </div>

                <div className="flex items-center space-x-2 bg-slate-50 dark:bg-slate-800 px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 cursor-pointer flex-1">
                  <RadioGroupItem value="PJ" id="create-pj" />
                  <Label htmlFor="create-pj" className="cursor-pointer font-semibold text-slate-800 dark:text-slate-200 flex items-center gap-1.5">
                    <FiBriefcase className="text-amber-600" /> Pessoa Jurídica (PJ)
                  </Label>
                </div>
              </RadioGroup>
            </div>

            {/* Documento (CPF / CNPJ com máscara) */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">
                  {walletForm.tipo === 'PJ' ? 'CNPJ' : 'CPF'}
                </Label>
                <Input
                  required
                  value={walletForm.documento}
                  onChange={(e) => setWalletForm({ ...walletForm, documento: maskDocument(e.target.value, walletForm.tipo) })}
                  placeholder={walletForm.tipo === 'PJ' ? '00.000.000/0000-00' : '000.000.000-00'}
                  className="mt-1"
                />
              </div>

              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">
                  {walletForm.tipo === 'PJ' ? 'Razão Social / Nome Fantasia' : 'Nome Completo'}
                </Label>
                <Input
                  required
                  value={walletForm.nome}
                  onChange={(e) => setWalletForm({ ...walletForm, nome: e.target.value })}
                  placeholder={walletForm.tipo === 'PJ' ? 'Ex: Minha Empresa LTDA' : 'Ex: João da Silva'}
                  className="mt-1"
                />
              </div>
            </div>

            {/* Bancos Vinculados (Lista Dinâmica) */}
            <div className="space-y-3 pt-3 border-t border-slate-100 dark:border-slate-800">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-200 flex items-center gap-1.5">
                  <FiCreditCard className="text-blue-600" /> Bancos Vinculados a esta Carteira
                </Label>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={handleAddBankToCreate}
                  className="text-xs gap-1 border-blue-600/30 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950"
                >
                  <FiPlus size={14} /> Adicionar outro banco
                </Button>
              </div>

              <div className="space-y-3">
                {walletForm.bancos.map((b, idx) => (
                  <div key={idx} className="p-3.5 bg-slate-50 dark:bg-slate-800/60 rounded-xl border border-slate-200/80 dark:border-slate-700 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-slate-600 dark:text-slate-300">
                        Banco #{idx + 1}
                      </span>
                      {walletForm.bancos.length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveBankFromCreate(idx)}
                          className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1"
                        >
                          <FiTrash2 size={13} /> Remover
                        </button>
                      )}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                      {/* Banco Select / Input */}
                      <div className="sm:col-span-2 lg:col-span-2">
                        <div className="flex items-center justify-between mb-1">
                          <Label className="text-[11px] font-semibold text-slate-500">Instituição Bancária</Label>
                          <button
                            type="button"
                            onClick={() => setBanksCatalogDialogOpen(true)}
                            className="text-[10px] text-blue-600 hover:text-blue-800 dark:text-blue-400 hover:underline font-semibold flex items-center gap-1"
                          >
                            <FiSettings size={11} /> Gerenciar / Apagar Bancos
                          </button>
                        </div>
                        <div>
                          <Select
                            value={b.bank_name}
                            onValueChange={(v) => handleBankChangeInCreate(idx, 'bank_name', v)}
                          >
                            <SelectTrigger className="h-9 text-xs bg-white dark:bg-slate-900">
                              <SelectValue placeholder="Selecione o banco" />
                            </SelectTrigger>
                            <SelectContent className="max-h-60">
                              {globalBanks.map((gb) => (
                                <SelectItem key={gb.bank_id} value={gb.name}>
                                  {gb.name}
                                </SelectItem>
                              ))}
                              {['Nubank', 'Itaú', 'Bradesco', 'Banco do Brasil', 'Santander', 'Caixa', 'Inter', 'C6 Bank', 'Sicredi', 'Sicoob', 'BTG Pactual']
                                .filter(s => !globalBanks.some(gb => gb.name?.toLowerCase() === s.toLowerCase()))
                                .map(s => (
                                  <SelectItem key={s} value={s}>{s}</SelectItem>
                                ))
                              }
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      {/* Agência */}
                      <div>
                        <Label className="text-[11px] font-semibold text-slate-500">Agência</Label>
                        <Input
                          value={b.agencia}
                          onChange={(e) => handleBankChangeInCreate(idx, 'agencia', e.target.value)}
                          placeholder="0001"
                          className="h-9 text-xs mt-1 bg-white dark:bg-slate-900"
                        />
                      </div>

                      {/* Conta */}
                      <div>
                        <Label className="text-[11px] font-semibold text-slate-500">Conta Corrente</Label>
                        <Input
                          value={b.conta}
                          onChange={(e) => handleBankChangeInCreate(idx, 'conta', e.target.value)}
                          placeholder="12345-6"
                          className="h-9 text-xs mt-1 bg-white dark:bg-slate-900"
                        />
                      </div>

                      {/* Saldo Inicial */}
                      <div className="sm:col-span-2 lg:col-span-4">
                        <Label className="text-[11px] font-semibold text-slate-500">Saldo Inicial (R$)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={b.saldo_inicial}
                          onChange={(e) => handleBankChangeInCreate(idx, 'saldo_inicial', e.target.value)}
                          placeholder="0,00"
                          className="h-9 text-xs mt-1 bg-white dark:bg-slate-900"
                        />
                        <p className="text-[10px] text-slate-400 mt-0.5">Saldo base a partir do qual as movimentações serão somadas/subtraídas.</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-3 border-t border-slate-100 dark:border-slate-800">
              <Button
                type="button"
                variant="outline"
                onClick={() => setCreateWalletOpen(false)}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-blue-900 hover:bg-blue-800 text-white font-semibold"
              >
                Salvar Carteira
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* ================= MODAL: EDITAR CARTEIRA ================= */}
      <Dialog open={editWalletOpen} onOpenChange={setEditWalletOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-blue-900 dark:text-blue-400 flex items-center gap-2">
              <FiEdit2 /> Editar Carteira
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Altere dados da carteira, adicione novos bancos vinculados ou altere saldos.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSaveEditedWallet} className="space-y-5 mt-4">
            {/* Select Carteira no Topo */}
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">
                Selecione a Carteira para Editar
              </Label>
              <Select
                value={editWalletForm.carteira_id}
                onValueChange={handleSelectWalletToEdit}
              >
                <SelectTrigger className="h-10 mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {carteiras.map(c => (
                    <SelectItem key={c.carteira_id} value={c.carteira_id}>
                      {c.tipo} - {c.nome} ({maskDocument(c.documento, c.tipo)})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Tipo: PF / PJ */}
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">
                Tipo
              </Label>
              <RadioGroup
                value={editWalletForm.tipo}
                onValueChange={(val) => setEditWalletForm({ ...editWalletForm, tipo: val, documento: maskDocument(editWalletForm.documento, val) })}
                className="flex gap-4 mt-2"
              >
                <div className="flex items-center space-x-2 bg-slate-50 dark:bg-slate-800 px-4 py-2 rounded-xl border border-slate-200 dark:border-slate-700 flex-1">
                  <RadioGroupItem value="PF" id="edit-pf" />
                  <Label htmlFor="edit-pf" className="cursor-pointer font-semibold text-slate-800 dark:text-slate-200">
                    Pessoa Física (PF)
                  </Label>
                </div>
                <div className="flex items-center space-x-2 bg-slate-50 dark:bg-slate-800 px-4 py-2 rounded-xl border border-slate-200 dark:border-slate-700 flex-1">
                  <RadioGroupItem value="PJ" id="edit-pj" />
                  <Label htmlFor="edit-pj" className="cursor-pointer font-semibold text-slate-800 dark:text-slate-200">
                    Pessoa Jurídica (PJ)
                  </Label>
                </div>
              </RadioGroup>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">
                  {editWalletForm.tipo === 'PJ' ? 'CNPJ' : 'CPF'}
                </Label>
                <Input
                  required
                  value={editWalletForm.documento}
                  onChange={(e) => setEditWalletForm({ ...editWalletForm, documento: maskDocument(e.target.value, editWalletForm.tipo) })}
                  className="mt-1"
                />
              </div>

              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">
                  Nome / Razão Social
                </Label>
                <Input
                  required
                  value={editWalletForm.nome}
                  onChange={(e) => setEditWalletForm({ ...editWalletForm, nome: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>

            {/* Bancos Vinculados */}
            <div className="space-y-3 pt-3 border-t border-slate-100 dark:border-slate-800">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-200">
                  Bancos Vinculados
                </Label>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={handleAddBankToEdit}
                  className="text-xs gap-1 border-blue-600/30 text-blue-600"
                >
                  <FiPlus size={14} /> Incluir novo banco
                </Button>
              </div>

              <div className="space-y-3">
                {editWalletForm.bancos.map((b, idx) => (
                  <div key={idx} className="p-3.5 bg-slate-50 dark:bg-slate-800/60 rounded-xl border border-slate-200/80 dark:border-slate-700 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-slate-600 dark:text-slate-300">
                        {b.bank_name || `Banco #${idx + 1}`}
                      </span>
                      {editWalletForm.bancos.length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveBankFromEdit(idx)}
                          className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1"
                        >
                          <FiTrash2 size={13} /> Remover
                        </button>
                      )}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                      <div className="sm:col-span-2 lg:col-span-2">
                        <div className="flex items-center justify-between mb-1">
                          <Label className="text-[11px] font-semibold text-slate-500">Instituição Bancária</Label>
                          <button
                            type="button"
                            onClick={() => setBanksCatalogDialogOpen(true)}
                            className="text-[10px] text-blue-600 hover:text-blue-800 dark:text-blue-400 hover:underline font-semibold flex items-center gap-1"
                          >
                            <FiSettings size={11} /> Gerenciar / Apagar Bancos
                          </button>
                        </div>
                        <Select
                          value={b.bank_name}
                          onValueChange={(v) => handleBankChangeInEdit(idx, 'bank_name', v)}
                        >
                          <SelectTrigger className="h-9 text-xs bg-white dark:bg-slate-900">
                            <SelectValue placeholder="Selecione o banco" />
                          </SelectTrigger>
                          <SelectContent className="max-h-60">
                            {globalBanks.map((gb) => (
                              <SelectItem key={gb.bank_id} value={gb.name}>
                                {gb.name}
                              </SelectItem>
                            ))}
                            {['Nubank', 'Itaú', 'Bradesco', 'Banco do Brasil', 'Santander', 'Caixa', 'Inter', 'C6 Bank', 'Sicredi', 'Sicoob', 'BTG Pactual']
                              .filter(s => !globalBanks.some(gb => gb.name?.toLowerCase() === s.toLowerCase()))
                              .map(s => (
                                <SelectItem key={s} value={s}>{s}</SelectItem>
                              ))
                            }
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label className="text-[11px] font-semibold text-slate-500">Agência</Label>
                        <Input
                          value={b.agencia}
                          onChange={(e) => handleBankChangeInEdit(idx, 'agencia', e.target.value)}
                          className="h-9 text-xs mt-1 bg-white dark:bg-slate-900"
                        />
                      </div>

                      <div>
                        <Label className="text-[11px] font-semibold text-slate-500">Conta Corrente</Label>
                        <Input
                          value={b.conta}
                          onChange={(e) => handleBankChangeInEdit(idx, 'conta', e.target.value)}
                          className="h-9 text-xs mt-1 bg-white dark:bg-slate-900"
                        />
                      </div>

                      <div className="sm:col-span-2 lg:col-span-4">
                        <Label className="text-[11px] font-semibold text-slate-500">Saldo Inicial (R$)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={b.saldo_inicial}
                          onChange={(e) => handleBankChangeInEdit(idx, 'saldo_inicial', e.target.value)}
                          className="h-9 text-xs mt-1 bg-white dark:bg-slate-900"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-wrap items-center justify-between gap-3 pt-3 border-t border-slate-100 dark:border-slate-800">
              <Button
                type="button"
                variant="destructive"
                onClick={handleDeleteWallet}
                className="gap-1.5"
              >
                <FiTrash2 size={16} /> Excluir Carteira
              </Button>

              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setEditWalletOpen(false)}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  className="bg-blue-900 hover:bg-blue-800 text-white font-semibold"
                >
                  Salvar Alterações
                </Button>
              </div>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* ================= MODAL: TRANSFERÊNCIA INTERNA ================= */}
      <Dialog open={transferOpen} onOpenChange={setTransferOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-emerald-800 dark:text-emerald-400 flex items-center gap-2">
              <FiRepeat /> Transferência Interna
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Mova saldo entre duas contas bancárias da mesma carteira de forma automática.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleExecuteTransfer} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">
                  Banco de Origem (Saída)
                </Label>
                <Select
                  value={transferForm.banco_origem_id}
                  onValueChange={(v) => setTransferForm({ ...transferForm, banco_origem_id: v })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Selecione o banco de saída" />
                  </SelectTrigger>
                  <SelectContent>
                    {currentCarteira?.bancos?.map((b) => (
                      <SelectItem key={b.banco_item_id} value={b.banco_item_id} disabled={b.banco_item_id === transferForm.banco_destino_id}>
                        {b.bank_name} ({formatBRL(b.saldo_atual)})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">
                  Banco de Destino (Entrada)
                </Label>
                <Select
                  value={transferForm.banco_destino_id}
                  onValueChange={(v) => setTransferForm({ ...transferForm, banco_destino_id: v })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Selecione o banco de entrada" />
                  </SelectTrigger>
                  <SelectContent>
                    {currentCarteira?.bancos?.map((b) => (
                      <SelectItem key={b.banco_item_id} value={b.banco_item_id} disabled={b.banco_item_id === transferForm.banco_origem_id}>
                        {b.bank_name} ({formatBRL(b.saldo_atual)})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">
                  Valor da Transferência (R$)
                </Label>
                <Input
                  required
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={transferForm.valor}
                  onChange={(e) => setTransferForm({ ...transferForm, valor: e.target.value })}
                  placeholder="0,00"
                  className="mt-1"
                />
              </div>

              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">
                  Data
                </Label>
                <Input
                  type="date"
                  required
                  value={transferForm.data}
                  onChange={(e) => setTransferForm({ ...transferForm, data: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>

            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">
                Observação
              </Label>
              <Input
                value={transferForm.observacao}
                onChange={(e) => setTransferForm({ ...transferForm, observacao: e.target.value })}
                placeholder="Ex: Aporte, ajuste de caixa"
                className="mt-1"
              />
            </div>

            <div className="flex gap-3 pt-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setTransferOpen(false)}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-emerald-700 hover:bg-emerald-800 text-white font-semibold"
              >
                Confirmar Transferência
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* ================= MODAL: LANÇAMENTO RÁPIDO NA CARTEIRA ================= */}
      <Dialog open={quickMovementOpen} onOpenChange={setQuickMovementOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-blue-900 dark:text-blue-400">
              Novo Lançamento na Carteira
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Adicione uma receita ou despesa vinculada diretamente a um banco desta carteira.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSaveQuickMovement} className="space-y-4 mt-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">Tipo</Label>
              <RadioGroup
                value={quickMovForm.record_type}
                onValueChange={(val) => setQuickMovForm({ ...quickMovForm, record_type: val })}
                className="flex gap-4 mt-2"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="pagar" id="quick-pagar" />
                  <Label htmlFor="quick-pagar" className="cursor-pointer text-red-600 font-semibold">Saída (Despesa)</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="receber" id="quick-receber" />
                  <Label htmlFor="quick-receber" className="cursor-pointer text-blue-900 font-semibold">Entrada (Receita)</Label>
                </div>
              </RadioGroup>
            </div>

            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">Banco</Label>
              <Select
                value={quickMovForm.carteira_banco_id}
                onValueChange={(v) => setQuickMovForm({ ...quickMovForm, carteira_banco_id: v })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Selecione o banco" />
                </SelectTrigger>
                <SelectContent>
                  {currentCarteira?.bancos?.map((b) => (
                    <SelectItem key={b.banco_item_id} value={b.banco_item_id}>
                      {b.bank_name} ({formatBRL(b.saldo_atual)})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">Descrição</Label>
              <Input
                required
                value={quickMovForm.description}
                onChange={(e) => setQuickMovForm({ ...quickMovForm, description: e.target.value })}
                placeholder="Ex: Pagamento fornecedor, Venda"
                className="mt-1"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">Valor (R$)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0.01"
                  required
                  value={quickMovForm.value}
                  onChange={(e) => setQuickMovForm({ ...quickMovForm, value: e.target.value })}
                  placeholder="0,00"
                  className="mt-1"
                />
              </div>

              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">Data</Label>
                <Input
                  type="date"
                  required
                  value={quickMovForm.reference_date}
                  onChange={(e) => setQuickMovForm({ ...quickMovForm, reference_date: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>

            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">
                Conta / Categoria (Plano de Contas)
              </Label>
              <Select
                value={
                  quickMovForm.carteira_account_id
                    ? `account:${quickMovForm.carteira_account_id}`
                    : quickMovForm.carteira_group_id
                    ? `group:${quickMovForm.carteira_group_id}`
                    : (quickMovForm.category_name || '')
                }
                onValueChange={(val) => {
                  if (val.startsWith('group:')) {
                    const gid = val.replace('group:', '');
                    const allG = [...(accountsTree.entradas || []), ...(accountsTree.saidas || [])];
                    const g = allG.find(item => item.group_id === gid);
                    if (g) {
                      setQuickMovForm({
                        ...quickMovForm,
                        carteira_group_id: g.group_id,
                        carteira_group_name: g.name,
                        carteira_account_id: '',
                        carteira_account_name: '',
                        category_name: g.name
                      });
                    }
                  } else if (val.startsWith('account:')) {
                    const aid = val.replace('account:', '');
                    const allG = [...(accountsTree.entradas || []), ...(accountsTree.saidas || [])];
                    for (const g of allG) {
                      const acc = (g.accounts || []).find(a => a.account_id === aid);
                      if (acc) {
                        setQuickMovForm({
                          ...quickMovForm,
                          carteira_group_id: g.group_id,
                          carteira_group_name: g.name,
                          carteira_account_id: acc.account_id,
                          carteira_account_name: acc.name,
                          category_name: acc.name
                        });
                        break;
                      }
                    }
                  }
                }}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Selecione a conta no plano de contas" />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  {((quickMovForm.record_type === 'receber' ? accountsTree.entradas : accountsTree.saidas) || []).map((grp) => (
                    <React.Fragment key={grp.group_id}>
                      <SelectItem value={`group:${grp.group_id}`} className="font-bold text-slate-800 dark:text-slate-200 bg-slate-100/70 dark:bg-slate-800/80 my-0.5">
                        <div className="flex items-center gap-1.5">
                          <span>📁</span>
                          <span>{grp.name}</span>
                        </div>
                      </SelectItem>
                      {(grp.accounts || []).map((acc) => (
                        <SelectItem key={acc.account_id} value={`account:${acc.account_id}`} className="pl-6">
                          ↳ {acc.name}
                        </SelectItem>
                      ))}
                    </React.Fragment>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-3 pt-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setQuickMovementOpen(false)}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-blue-900 hover:bg-blue-800 text-white font-semibold"
              >
                Salvar Lançamento
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* ================= MODAL: GERENCIAR CATÁLOGO DE BANCOS ================= */}
      <CrudListDialog
        open={banksCatalogDialogOpen}
        onOpenChange={setBanksCatalogDialogOpen}
        title="Catálogo de Instituições Bancárias"
        placeholder="Nome da instituição (ex: BTG Pactual, Wise, Nomad)"
        newValue={newCatalogBankName}
        onNewValueChange={setNewCatalogBankName}
        onAdd={handleAddCatalogBank}
        items={globalBanks.map((gb) => ({ id: gb.bank_id || gb._id, name: gb.name }))}
        onDelete={handleDeleteCatalogBank}
        testIdPrefix="bank-catalog"
        description="Adicione ou remova instituições bancárias do seu catálogo. As alterações refletem imediatamente nas carteiras."
      />

    </div>
  );
};
